/**
 * Home's To Do list (2026-09-24): what the business has to do, and by when.
 *
 * THE ROWS ARE THE SERVER'S. Which there are, what each says, their order,
 * which the Home tab counts, and each return's period are decided in
 * apps/api/src/todo/list.ts. This screen filters them, and records the one
 * thing on the list the books cannot say is done: that a return was filed.
 *
 * THE COLUMNS, AS THE OPERATOR ASKED THEM (2026-09-24, from the first look at
 * production):
 *
 *   Title     what it is, leading to the screen that does it.
 *   Category  on All only: Federal, State or Books.
 *   Kind      on Books only: Uncategorized, Payroll, Reconciliation...
 *   When      a badge and nothing else -- Overdue, Now, Due Soon, Due, Filed.
 *             A badge beside a date read badly, so the date moved out; and the
 *             three the Home tab counts (Overdue, Now, Due Soon) are the three
 *             that are coloured, so the tab's number can be read off the list.
 *   Deadline  the date it is due by. Where a weekend or holiday moved it, an
 *             info button beside it holds the day it moved from: in its
 *             tooltip, as the operator asked (2026-09-24, "(was ...)" beside
 *             every moved date was text to read on every row), and in a note on
 *             a click or a tap, since a phone has no hover.
 *             Blank where there is none, which is every waiting row but a
 *             commitment's, an invoice's and a year's figures: a pay run's pay
 *             date is in its sentence, because a pay date is not a deadline.
 *   What      the sentence, and Mark Filed on a return.
 *
 * THE FILTERS COME FROM THE ROWS. A category is offered when a row has it, so
 * Local appears the day a source for it does, and the finer choice of kind is
 * offered only inside Books, where there are enough kinds to want it.
 *
 * THE HORIZON IS HOME'S, AND EVERY ROW OBEYS IT. A row due inside it is shown;
 * one overdue or waiting always is, since no window makes overdue work less
 * overdue; and a return recorded as filed is shown on request, inside it too.
 * For one deploy the next return of each form was listed whatever the horizon,
 * because Q3's returns fell outside thirty days on the first look at production
 * and Mark Filed could not be found. The operator reversed it the same day
 * (2026-09-24): "if 30 days is selected why would we include february
 * deadlines". A return is listed once the horizon reaches its deadline, so a
 * longer horizon lists it sooner.
 *
 * MARK FILED FROM THE DAY AFTER THE PERIOD ENDS. A return cannot be filed
 * before its period is over, and 0050 refuses one dated earlier, so the button
 * is not offered until then; the row's sentence says when the period ends.
 *
 * SHOW FILED RETURNS IS OFFERED WHERE THE VIEW HAS RETURNS -- All, Federal and
 * State, never Books -- and answers with a sentence when it shows none: that
 * none is filed yet, or that the ones filed fall due beyond the horizon. It was
 * once offered only after something was filed, and the operator looked for it
 * and could not find it; and on Books, where no return is, it did nothing at
 * all. A control that answers with a sentence is the rule here, not one that
 * is missing or silent.
 *
 * MARK FILED ASKS FOR THE DATE. It defaults to today, which is when most
 * returns are marked, and it is a field because the day a return went in is the
 * record -- a Form 941 filed on the 28th and marked on the 3rd was not late.
 */

import { useCan } from '../auth/AuthProvider.js';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError, api, type ReturnForm, type TodoCategory, type TodoGroup, type TodoItem, type TodoList } from '../api.js';
import { daysAfter } from '../lib/cashflow.js';
import { formatCents } from '../lib/money.js';
import { InfoNote } from './InfoNote.js';
import { Toast } from './Toast.js';

const CATEGORY_LABELS: Record<TodoCategory, string> = {
  federal: 'Federal',
  state: 'State',
  books: 'Books',
};
const CATEGORIES: readonly TodoCategory[] = ['federal', 'state', 'books'];

const GROUP_LABELS: Record<TodoGroup, string> = {
  returns: 'Returns',
  deposits: 'Deposits',
  uncategorized: 'Uncategorized',
  drafts: 'Drafts',
  payments: 'Payments',
  payroll: 'Payroll',
  bills: 'Bills',
  invoices: 'Invoices',
  commitments: 'Commitments',
  reconciliation: 'Reconciliation',
  sync: 'Sync',
  setup: 'Setup',
  releases: 'Releases',
};

/** The When badge: the row's state, coloured when the Home tab counts it. */
/** 0095: a C corporation's estimated tax installment is paid, not filed, so its row says so. */
function isPayment(item: TodoItem): boolean {
  return item.filing?.form === 'form_1120_estimate';
}

function badgeOf(item: TodoItem): { label: string; tag: string } {
  if (item.status === 'overdue') return { label: 'Overdue', tag: 'tag tag-overdue' };
  if (item.status === 'now') return { label: 'Now', tag: 'tag tag-due' };
  if (item.status === 'done') return { label: 'Filed', tag: 'tag tag-known' };
  return item.counted ? { label: 'Due Soon', tag: 'tag tag-due' } : { label: 'Due', tag: 'tag' };
}

/**
 * Whether a row belongs on screen under this horizon. What is overdue or
 * waiting always does. A row due, and a return filed, when its deadline is
 * inside the horizon -- a filed one only when asked for.
 */
function inWindow(item: TodoItem, horizonEnd: string, showDone: boolean): boolean {
  if (item.status === 'overdue' || item.status === 'now') return true;
  if (item.status === 'done' && !showDone) return false;
  return item.when !== null && item.when <= horizonEnd;
}

interface Filing {
  item: TodoItem;
  filedOn: string;
  note: string;
  error: string | null;
  busy: boolean;
}

export function TodoSection({
  todo,
  horizon,
  horizonLabel,
  reload,
}: {
  todo: TodoList;
  horizon: number;
  horizonLabel: string;
  reload: () => Promise<void>;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filing, setFiling] = useState<Filing | null>(null);
  const [toast, setToast] = useState<{ message: string; undo?: () => void } | null>(null);

  const categoryParam = searchParams.get('todo');
  const kindParam = searchParams.get('kind');
  const showDone = searchParams.get('done') === '1';

  /** One update for every key given: two calls would each start from the same snapshot, and the first would be lost. */
  function setParams(changes: Record<string, string>) {
    const params = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(changes)) {
      if (value === '') params.delete(key);
      else params.set(key, value);
    }
    setSearchParams(params, { replace: true });
  }

  const horizonEnd = daysAfter(todo.today, horizon);
  const visible = todo.items.filter((item) => inWindow(item, horizonEnd, showDone));
  const categories = CATEGORIES.filter((category) => visible.some((item) => item.category === category));
  const category = categories.find((each) => each === categoryParam) ?? null;
  const inCategory = category === null ? visible : visible.filter((item) => item.category === category);
  const kinds = [...new Set(inCategory.map((item) => item.group))];
  const kind = category === 'books' ? (kinds.find((each) => each === kindParam) ?? null) : null;
  const rows = kind === null ? inCategory : inCategory.filter((item) => item.group === kind);
  // The view's returns whatever the horizon, for Show Filed Returns (see the header).
  const returns = todo.items.filter((item) => item.filing !== null && (category === null || item.category === category));
  const filed = returns.filter((item) => item.status === 'done');
  const filedShown = filed.filter((item) => inWindow(item, horizonEnd, true)).length;
  // The extra column each view wants: the category on All, the kind on Books.
  const extra: 'category' | 'kind' | null = category === null ? 'category' : category === 'books' ? 'kind' : null;

  async function record(current: Filing) {
    const { filing: target } = current.item;
    if (target === null) return;
    setFiling({ ...current, busy: true, error: null });
    try {
      const { filing: recorded } = await api.recordReturnFiling({
        form: target.form,
        year: target.year,
        quarter: target.quarter,
        filedOn: current.filedOn,
        note: current.note.trim() === '' ? null : current.note.trim(),
      });
      setFiling(null);
      await reload();
      setToast({
        message: `${current.item.title} is recorded as ${isPayment(current.item) ? 'paid' : 'filed'} on ${recorded.filedOn}.`,
        undo: () => void withdraw(current.item.title, recorded.id, target.form),
      });
    } catch (caught) {
      setFiling({
        ...current,
        busy: false,
        error: caught instanceof ApiError ? caught.message : 'Could not record the filing.',
      });
    }
  }

  async function withdraw(title: string, filingId: string, form: ReturnForm) {
    try {
      await api.withdrawReturnFiling(filingId, form);
      await reload();
      setToast({ message: `${title} is on the list again.` });
    } catch (caught) {
      setToast({ message: caught instanceof ApiError ? caught.message : 'Could not take the filing back.' });
    }
  }

  return (
    <section className="todo" aria-labelledby="todo-heading">
      <h2 id="todo-heading">To Do</h2>

      {/*
        THE VIEW YOU ARE IN, so tabs: the Triage screen's statuses are the
        same kind of choice. Counted, so a category with work in it says so
        before it is opened.
      */}
      <nav className="row tabs" aria-label="To Do Category">
        <button
          type="button"
          className={`tab${category === null ? ' is-current' : ''}`}
          aria-current={category === null}
          onClick={() => setParams({ todo: '', kind: '' })}
        >
          All <span className="muted">{visible.length}</span>
        </button>
        {categories.map((each) => (
          <button
            key={each}
            type="button"
            className={`tab${category === each ? ' is-current' : ''}`}
            aria-current={category === each}
            onClick={() => setParams({ todo: each, kind: '' })}
          >
            {CATEGORY_LABELS[each]}{' '}
            <span className="muted">{visible.filter((item) => item.category === each).length}</span>
          </button>
        ))}
      </nav>

      <div className="row triage-filters">
        {category === 'books' && kinds.length > 1 && (
          <label>
            Kind{' '}
            <select aria-label="Kind" value={kind ?? ''} onChange={(event) => setParams({ kind: event.target.value })}>
              <option value="">All Books</option>
              {kinds.map((each) => (
                <option key={each} value={each}>
                  {GROUP_LABELS[each]}
                </option>
              ))}
            </select>
          </label>
        )}
        {returns.length > 0 && (
          <label>
            <input type="checkbox" checked={showDone} onChange={(event) => setParams({ done: event.target.checked ? '1' : '' })} />{' '}
            Show Filed Returns
          </label>
        )}
      </div>

      {showDone && returns.length > 0 && filedShown === 0 && (
        <p className="muted">
          {filed.length === 0
            ? 'No return has been recorded as filed yet.'
            : `The returns recorded as filed fall due after the ${horizonLabel.toLowerCase()}. A longer horizon lists them.`}
        </p>
      )}

      {rows.length === 0 ? (
        /*
          ONE EMPTY STATE. A filter cannot empty the list: a category or kind is
          offered only while a row has it, and one named in the URL that no row
          has reads as All.
        */
        <p className="muted">
          Nothing is overdue or waiting, and nothing falls due in the {horizonLabel.toLowerCase()}.
        </p>
      ) : (
        <div className="table-scroll">
          <table className="lines todo-list">
            <thead>
              <tr>
                <th>Title</th>
                {extra === 'category' && <th>Category</th>}
                {extra === 'kind' && <th>Kind</th>}
                <th>When</th>
                <th>Deadline</th>
                <th>What</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((item) => (
                <TodoRow
                  key={item.key}
                  item={item}
                  extra={extra}
                  fileable={item.filing !== null && todo.today > item.filing.periodEnd}
                  filing={filing?.item.key === item.key ? filing : null}
                  onMarkFiled={() => setFiling({ item, filedOn: todo.today, note: '', error: null, busy: false })}
                  onNotFiled={() => void withdraw(item.title, item.filed!.id, item.filing!.form)}
                  onChange={(changed) => setFiling(changed)}
                  onRecord={(current) => void record(current)}
                  onCancel={() => setFiling(null)}
                  onReleased={(message) => {
                    void reload();
                    setToast({ message });
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          variant="ok"
          onDismiss={() => setToast(null)}
          action={toast.undo ? { label: 'Undo', onAction: toast.undo } : undefined}
        />
      )}
    </section>
  );
}

function TodoRow({
  item,
  extra,
  fileable,
  filing,
  onMarkFiled,
  onNotFiled,
  onChange,
  onRecord,
  onCancel,
  onReleased,
}: {
  item: TodoItem;
  extra: 'category' | 'kind' | null;
  /** A return whose period has ended, so it can be recorded as filed. */
  fileable: boolean;
  filing: Filing | null;
  onMarkFiled: () => void;
  onNotFiled: () => void;
  onChange: (next: Filing) => void;
  onRecord: (current: Filing) => void;
  onCancel: () => void;
  /** 0089: a fund released, with the sentence to say. */
  onReleased: (message: string) => void;
}) {
  // Recording a return as filed is running payroll's (0069).
  const can = useCan();
  const mayFile = can('run_payroll');
  // 0089 (§B.19, round 136's Q1): a release moves net assets between classes, as the close does.
  const mayRelease = can('close_periods');
  const [releasing, setReleasing] = useState<{ date: string; busy: boolean; error: string | null } | null>(null);

  async function release(current: { date: string }) {
    if (item.release === null) return;
    setReleasing({ date: current.date, busy: true, error: null });
    try {
      const { release: done } = await api.releaseFund(item.release.fundId, current.date);
      setReleasing(null);
      onReleased(`${formatCents(done.amountCents)} is released from restriction, and the fund is archived.`);
    } catch (caught) {
      setReleasing({ date: current.date, busy: false, error: caught instanceof ApiError ? caught.message : 'Could not release the fund.' });
    }
  }
  const badge = badgeOf(item);
  const columns = 5 + (extra === null ? 0 : 1);
  return (
    <>
      <tr className={item.status === 'done' ? 'is-done' : undefined}>
        <td className="todo-title">{item.link === null ? item.title : <Link to={item.link}>{item.title}</Link>}</td>
        {extra === 'category' && <td className="todo-meta">{CATEGORY_LABELS[item.category]}</td>}
        {extra === 'kind' && <td className="todo-meta">{GROUP_LABELS[item.group]}</td>}
        <td className="todo-when">
          <span className={badge.tag}>{badge.label}</span>
        </td>
        <td className="todo-deadline">
          {item.when !== null && <span className="date-cell">{item.when}</span>}
          {item.when !== null && item.movedFrom !== null && (
            <>
              {' '}
              {/* Its title is the tooltip; anchored, as a note in a table cell must be. */}
              <InfoNote label={`Moved From ${item.movedFrom}`} anchored>
                The rule's date is {item.movedFrom}. A weekend or a legal holiday moved it to {item.when}.
              </InfoNote>
            </>
          )}
        </td>
        <td className="todo-what">
          {item.status === 'done' ? (
            <>
              {isPayment(item) ? 'Paid' : 'Filed'} {item.filed!.on}.{item.filed!.note && <span className="muted"> {item.filed!.note}</span>}{' '}
              {mayFile && (
                <button type="button" className="link" onClick={onNotFiled}>
                  {isPayment(item) ? 'Not Paid' : 'Not Filed'}
                </button>
              )}
            </>
          ) : (
            <>
              {item.what}{' '}
              {fileable && mayFile && filing === null && (
                <button type="button" className="link" onClick={onMarkFiled}>
                  {isPayment(item) ? 'Mark Paid' : 'Mark Filed'}
                </button>
              )}
              {item.release !== null && mayRelease && releasing === null && (
                <button type="button" className="link" onClick={() => setReleasing({ date: item.release!.releaseOn, busy: false, error: null })}>
                  Release It
                </button>
              )}
            </>
          )}
        </td>
      </tr>
      {releasing !== null && (
        <tr className="todo-filing">
          <td colSpan={columns}>
            {/* The Matching screen's inline form, as Mark Filed's is. */}
            <div className="new-account undo-form">
              <label className="field">
                <span>Release On</span>
                <input
                  type="date"
                  aria-label={`Date ${item.title} takes effect`}
                  value={releasing.date}
                  onChange={(event) => {
                    const date = event.target.value;
                    setReleasing((now) => (now === null ? now : { ...now, date }));
                  }}
                />
              </label>
              <div className="row">
                <button type="button" disabled={releasing.busy} onClick={() => void release(releasing)}>
                  Release The Fund
                </button>
                <button type="button" className="link" onClick={() => setReleasing(null)}>
                  Cancel
                </button>
              </div>
              {releasing.error !== null && (
                <p role="alert" className="error">
                  {releasing.error}
                </p>
              )}
            </div>
          </td>
        </tr>
      )}
      {filing !== null && (
        <tr className="todo-filing">
          <td colSpan={columns}>
            {/* The Matching screen's inline form, as every confirmation here is. */}
            <div className="new-account undo-form">
              <label className="field">
                <span>{isPayment(item) ? 'Paid On' : 'Filed On'}</span>
                <input
                  type="date"
                  aria-label={`Date ${item.title} was ${isPayment(item) ? 'paid' : 'filed'}`}
                  value={filing.filedOn}
                  onChange={(event) => {
                    const filedOn = event.target.value;
                    onChange({ ...filing, filedOn });
                  }}
                />
              </label>
              <label className="field todo-note">
                <span>Note</span>
                <input
                  aria-label="Confirmation number or how it was filed"
                  placeholder="Optional: a confirmation number"
                  maxLength={200}
                  value={filing.note}
                  onChange={(event) => {
                    const note = event.target.value;
                    onChange({ ...filing, note });
                  }}
                />
              </label>
              <div className="row">
                <button type="button" disabled={filing.busy} onClick={() => onRecord(filing)}>
                  {isPayment(item) ? 'Record as Paid' : 'Record as Filed'}
                </button>
                <button type="button" className="link" onClick={onCancel}>
                  Cancel
                </button>
              </div>
              {filing.error !== null && (
                <p role="alert" className="error">
                  {filing.error}
                </p>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
